/**
 * Copyright 2024 Google LLC
 *
 * Licensed under the Apache License, Version 2.0 (the "License");
 * you may not use this file except in compliance with the License.
 * You may obtain a copy of the License at
 *
 *     http://www.apache.org/licenses/LICENSE-2.0
 *
 * Unless required by applicable law or agreed to in writing, software
 * distributed under the License is distributed on an "AS IS" BASIS,
 * WITHOUT WARRANTIES OR CONDITIONS OF ANY KIND, either express or implied.
 * See the License for the specific language governing permissions and
 * limitations under the License.
 */

import { useRef, useState, useEffect } from "react";
import "./App.scss";
import { LiveAPIProvider } from "./contexts/LiveAPIContext";
import { Altair } from "./components/altair/Altair";
import ControlTray from "./components/control-tray/ControlTray";
import cn from "classnames";

// WebSocket URI for our proxy server
const uri = process.env.REACT_APP_WORKER_URL || "ws://localhost:8080";

// Format seconds into MM:SS format
const formatTime = (seconds: number): string => {
  const minutes = Math.floor(seconds / 60);
  const remainingSeconds = seconds % 60;
  return `${minutes.toString().padStart(2, '0')}:${remainingSeconds.toString().padStart(2, '0')}`;
};

// Mock customer data
const mockCustomer = {
  id: "PROS-12345",
  name: "John Smith",
  email: "john.smith@acmecorp.com",
  phone: "+1 (555) 123-4567",
  company: "Acme Corporation",
  industry: "Retail",
  lastContact: "2023-12-15",
  salesStage: "Evaluation",
  previousMeetings: 2,
};

function App() {
  // this video reference is used for displaying the active stream, whether that is the webcam or screen capture
  const videoRef = useRef<HTMLVideoElement>(null);
  // either the screen capture, the video or null, if null we hide it
  const [videoStream, setVideoStream] = useState<MediaStream | null>(null);
  const [callActive, setCallActive] = useState(false);
  const [callTime, setCallTime] = useState(0);
  const [customer, setCustomer] = useState<typeof mockCustomer | null>(null);

  // Call timer effect
  useEffect(() => {
    let interval: NodeJS.Timeout | null = null;

    if (callActive) {
      interval = setInterval(() => {
        setCallTime(prevTime => prevTime + 1);
      }, 1000);
      // Set mock customer data when call becomes active
      setCustomer(mockCustomer);
    } else {
      setCallTime(0);
      setCustomer(null);
    }

    return () => {
      if (interval) clearInterval(interval);
    };
  }, [callActive]);

  return (
    <div className="App">
      <LiveAPIProvider url={uri}>
        <div className="call-center-console">
          <header className="call-center-header">
            <div className="logo">
              <h1>Confluent Sales Simulator</h1>
              <span className={cn("status-indicator", { active: callActive })}>
                {callActive ? "Training Session Active" : "Ready for Training"}
              </span>
            </div>
            <div className="call-info">
              {callActive && (
                <>
                  <div className="call-timer">{formatTime(callTime)}</div>
                  <button
                    className="end-call-btn"
                    onClick={() => setCallActive(false)}
                  >
                    End Session
                  </button>
                </>
              )}
            </div>
          </header>

          <main>
            <div className="main-app-area">
              <div className="call-center-content">
                {!callActive && (
                  <div className="roleplay-branding">
                    <h1>ROLEPLAY AI</h1>
                    <h2>Confluent Sales Training Simulator</h2>
                    <p>Press the call button to begin a new training session</p>
                  </div>
                )}

                {customer && (
                  <div className="customer-info-panel">
                    <h2>Prospect Information</h2>
                    <div className="customer-details">
                      <div className="detail-group">
                        <div className="detail-item">
                          <span className="label">ID:</span>
                          <span className="value">{customer.id}</span>
                        </div>
                        <div className="detail-item">
                          <span className="label">Name:</span>
                          <span className="value">{customer.name}</span>
                        </div>
                        <div className="detail-item">
                          <span className="label">Email:</span>
                          <span className="value">{customer.email}</span>
                        </div>
                        <div className="detail-item">
                          <span className="label">Phone:</span>
                          <span className="value">{customer.phone}</span>
                        </div>
                      </div>
                      <div className="detail-group">
                        <div className="detail-item">
                          <span className="label">Company:</span>
                          <span className="value account-type">{customer.company}</span>
                        </div>
                        <div className="detail-item">
                          <span className="label">Industry:</span>
                          <span className="value">{customer.industry}</span>
                        </div>
                        <div className="detail-item">
                          <span className="label">Sales Stage:</span>
                          <span className="value">{customer.salesStage}</span>
                        </div>
                        <div className="detail-item">
                          <span className="label">Previous Meetings:</span>
                          <span className="value">{customer.previousMeetings}</span>
                        </div>
                      </div>
                    </div>
                  </div>
                )}

                <div className="agent-response-area">
                  <Altair />
                </div>

                <video
                  className={cn("stream", {
                    hidden: !videoRef.current || !videoStream,
                  })}
                  ref={videoRef}
                  autoPlay
                  playsInline
                />
              </div>
            </div>

            <ControlTray
              videoRef={videoRef}
              supportsVideo={true}
              onVideoStreamChange={(stream) => {
                setVideoStream(stream);
                if (stream && !callActive) {
                  setCallActive(true);
                }
              }}
            >
              {!callActive && (
                <button
                  className="action-button answer-call-btn"
                  onClick={() => setCallActive(true)}
                >
                  <span className="material-symbols-outlined filled">call</span>
                </button>
              )}
            </ControlTray>
          </main>
        </div>
      </LiveAPIProvider>
    </div>
  );
}

export default App;
